import { useState, MouseEvent, ChangeEvent, ReactNode } from 'react';
import { CompositeDecorator, Editor, EditorState, RichUtils, Modifier, convertToRaw } from 'draft-js';
import 'draft-js/dist/Draft.css';
import { Box, Grid } from '@mui/material';
import { RichTextControls } from '@components/richTextEditor/RichTextControls';
import { AddLinkDialog } from '@components/richTextEditor/dialogs/AddLinkDialog';
import { URLValue } from '@components/richTextEditor/URLValue.type';
import { containerEditor, PAGE_COPY_INLINE_STYLES } from './RichTextEditor.styles';
import { RichTextFormat, useConvertEditorState } from './RichTextEditor.hooks';
import { contentStateToPageCopyHtml } from './pageCopyHtml';

type LinkType = {
  contentState: any;
  entityKey: string;
  decoratedText: string;
  children: ReactNode;
};

function findLinkEntities(contentBlock: any, callback: Function, contentState: any) {
  contentBlock.findEntityRanges((character: any) => {
    const entityKey = character.getEntity();
    return entityKey !== null && contentState.getEntity(entityKey).getType() === 'LINK';
  }, callback);
}

// Render `children` (Draft's leaf spans), not the plain decorated text: Draft maps the browser
// selection through those spans, so a selection inside a link would otherwise jump to the block start.
const Link = ({ contentState, entityKey, children }: LinkType) => {
  const { url, href } = contentState.getEntity(entityKey).getData();
  return <a href={url ?? href}>{children}</a>;
};

interface IRichTextEditor {
  isDisabled?: boolean;
  placeholder?: string;
  setContextValue: (data: any) => void;
  value?: any;
  hideControls?: boolean;
  /**
   * `raw` (default) stores Draft.js JSON. `html` stores the page-copy HTML subset (paragraphs, bold,
   * italics, links, lists) and adds list buttons — for public page copy the API sanitizes as HTML.
   */
  format?: RichTextFormat;
}

const linkDecorator = new CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: Link,
  },
]);

export function RichTextEditor({
  value,
  isDisabled = false,
  placeholder = '',
  setContextValue,
  hideControls = false,
  format = 'raw',
}: IRichTextEditor) {
  const [editorState, setEditorStateOnly] = useConvertEditorState(value, format, linkDecorator);
  const isHtml = format === 'html';

  // In html mode report edits as they happen (dirty state, length feedback), but only when the content
  // changed: Draft also fires onChange for focus and selection, and reporting then would mark the page
  // dirty on a click. The hook keeps this editor state when the same HTML is echoed back through `value`.
  const setEditorState = (nextState: InstanceType<typeof EditorState>) => {
    setEditorStateOnly(nextState);
    if (isHtml && nextState.getCurrentContent() !== editorState.getCurrentContent()) {
      setContextValue(contentStateToPageCopyHtml(nextState.getCurrentContent()));
    }
  };

  const [urlState, setUrlState] = useState<URLValue>({
    showURLInput: false,
    urlValue: { text: '', link: '' },
  });

  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const handleFocus = () => setIsEditorFocused(true);
  const handleBlur = () => {
    // html mode has already reported every content change (see setEditorState).
    if (!isHtml) setContextValue(JSON.stringify(convertToRaw(editorState.getCurrentContent())));
    setIsEditorFocused(false);
  };

  // ADD LINK
  const updateLink = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      const contentState = editorState.getCurrentContent();
      const startKey = selection.getStartKey();
      const startOffset = selection.getStartOffset();
      const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
      const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
      let link = '';
      let text = '';
      const anchorKey = selection.getAnchorKey();
      const currentBlock = contentState.getBlockForKey(anchorKey);
      const endOffset = selection.getEndOffset();
      const selectedText = currentBlock.getText().slice(startOffset, endOffset);
      text = selectedText;
      if (linkKey) {
        const linkInstance = contentState.getEntity(linkKey);
        // Links imported from HTML may carry only a URL; fall back to the selected text for the label.
        text = linkInstance.getData().text ?? selectedText;
        link = linkInstance.getData().url ?? linkInstance.getData().href ?? '';
      }
      setUrlState({ showURLInput: true, urlValue: { text, link } });
    }
  };

  const confirmLink = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', {
      text: urlState.urlValue.text,
      url: urlState.urlValue.link,
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const textWithEntity = Modifier.replaceText(
      contentState,
      editorState.getSelection(),
      urlState.urlValue.text,
      editorState.getCurrentInlineStyle(),
      entityKey,
    );
    const newState = EditorState.createWithContent(textWithEntity, linkDecorator);
    setEditorState(newState);
    setUrlState({ showURLInput: false, urlValue: { text: '', link: '' } });
  };

  const removeLink = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      setEditorState(RichUtils.toggleLink(editorState, selection, null));
    }
  };

  const onURLChange = (e: ChangeEvent<HTMLInputElement>) =>
    setUrlState((prev) => ({
      ...prev,
      urlValue: { ...prev.urlValue, [e.target.name]: e.target.value },
    }));

  const closeLinkDialog = () => setUrlState((prev) => ({ ...prev, showURLInput: false }));

  // ADD STYLES
  const updateStyle = (style: string) => {
    const nextState = RichUtils.toggleInlineStyle(editorState, style);
    setEditorState(nextState);
  };

  // LISTS (html format only)
  const toggleBlockType = (blockType: string) => {
    setEditorState(RichUtils.toggleBlockType(editorState, blockType));
  };

  return (
    <>
      <Grid
        sx={(theme) => ({
          minHeight: '112px',
          outline: isEditorFocused
            ? `2px solid ${theme.palette.secondary.main}`
            : `1px solid ${theme.palette.text.subdued}`,
          boxSizing: 'border-box',
          borderRadius: theme.shape.borderRadius,
          color: theme.palette.text.disabled,
          outlineColor: isDisabled ? theme.palette.text.disabled : '',
        })}
      >
        <Box
          sx={(theme) => ({
            backgroundColor: theme.palette.background.main,
            borderBottom: isDisabled ? '0' : theme.borders.main,
            borderTopRightRadius: 1,
            borderTopLeftRadius: 1,
          })}
        >
          {hideControls ? null : (
            <RichTextControls
              updateStyle={updateStyle}
              updateLink={updateLink}
              isDisabled={isDisabled}
              {...(isHtml ? { inlineStyles: PAGE_COPY_INLINE_STYLES, toggleBlockType } : {})}
            />
          )}
          <AddLinkDialog
            onURLChange={onURLChange}
            urlState={urlState}
            confirmLink={confirmLink}
            closeLinkDialog={closeLinkDialog}
            removeLink={removeLink}
          />
        </Box>
        <Grid
          sx={{
            marginBottom: '25px',
            padding: '10px',
            '& a': { color: 'secondary.main' },
            color: isDisabled ? 'text.disabled' : 'text.primary',
            fontWeight: 'normal',
            ...containerEditor,
          }}
        >
          <Editor
            spellCheck
            onFocus={handleFocus}
            onBlur={handleBlur}
            editorState={editorState}
            onChange={setEditorState}
            placeholder={placeholder}
            readOnly={isDisabled}
          />
        </Grid>
      </Grid>
    </>
  );
}
